<template>
	<div class="min-h-screen bg-neutral-900 bg-resorts-pattern-dark">
		<div v-if="store.state.loading" class="flex items-center justify-center min-h-screen">
			<p class="text-lg text-center text-white/50">{{ t('loading') }}</p>
		</div>

		<template v-else>
			<RouterView v-slot="{ Component }">
				<component :is="Component" ref="view" />
			</RouterView>
		</template>
	</div>
</template>

<script lang="ts" setup>
import './style.css';
import {nextTick, onMounted, onUnmounted, ref} from 'vue';
import { useI18n } from 'vue-i18n';
import bridge, {AppEvents} from '@thermenresorts/bridge';
import {useRouter} from 'vue-router';
import useStore from './store';
import {useApi} from './api';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const api = useApi();
const view = ref();

const events: AppEvents = {
  'pre-reservation-flow.open': async (contents) => {
    const resort = contents.resort || contents.workspace;
    const hasSelectedResort = resort && resort !== 'thermenresorts';
    store.state.canChooseResort = !hasSelectedResort;

    store.setGlobalFilters({
      persons_count: contents.persons_count,
      departure_date: contents.departure_date,
    });

    store.setInitialArrivalDate(contents.arrival_date);

    const date = store.state.arrivalDate ? store.state.arrivalDate : 'none';

    // #4774: only proceed with a resort that actually exists; a missing, default or
    // mis-embedded ref goes straight to the resort-choice page instead of pushing an
    // invalid route (which would redirect-chain or reload via router.go(0) below).
    if (!resort || !store.hasResort(resort)) {
      await router.push({ name: 'resort-choice' });
      return;
    }

    if (hasSelectedResort) {
      await router.push({
        name: 'resort-date',
        params: {
          resort,
          date,
        }
      });
    }

    if (contents.screen) {
      // Check if we have correct values
      if (['choices', 'thermen', 'hotel'].includes(contents.screen)) {
        if (contents.screen !== 'choices') {
          const mapping = {
            'thermen': 'mijn-thermen-thermen-entrees',
            'hotel': 'mijn-thermen-hotelovernachting-incl-sauna'
          } as const;

          const requiredId = mapping[contents.screen as keyof typeof mapping] || null;

          if (requiredId && resort) {
            try {
              const resortObj = store.getResort(resort);
              if (!resortObj) throw new Error(`${resort} is not a valid resort`);
              const response = await api.reserve.get(resortObj.id);
              const cards = response?.data?.data?.cards ?? [];
              const correctCard = cards.find(card => card.id === requiredId);

              // Whenever we have the correct card, send it to the view, otherwise give the choice overview on resort-reserve
              if (correctCard) {
                await router.push({
                  name: 'resort-options',
                  params: {
                    resort,
                    date,
                    productset_id: correctCard.product_set_id,
                  },
                });

                return;
              }
            } catch (error) {
              console.error(error);
            }
          }
        }

        await router.push({
          name: 'resort-reserve',
          params: {
            resort,
            date,
          }
        });
        router.go(0);
      }
    }

    await nextTick();

    if (contents.voucherPopup) {
      view.value?.revealVoucherPopup?.();
    } else {
      // hideVoucherPopup is only available in the `Date` view. So check if the function exists to prevent errors.
      view.value?.hideVoucherPopup?.();
    }
  },
};

onMounted(async () => {
  await store.getResorts();

  bridge.receiveMessages('pre-reservation-flow', events);

  /**
   * The app is directly loaded in an iframe and in some cases the
   * 'pre-reservation-flow.ready' is send before the website is ready to receive them.
   * So what we do here is send a broadcast messages over a period of time to make sure the app is ready.
   */

  let broadcasts = 0;
  const broadcastInterval = setInterval(() => {
    bridge.sendMessage('pre-reservation-flow', 'pre-reservation-flow.ready'); // App is ready to receive messages.
    broadcasts++;
    if (broadcasts > 10) clearInterval(broadcastInterval);
  }, 500);
});

onUnmounted(() => {
  bridge.stopMessages();
});
</script>
